import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Scan, Coins, Gift, CheckCircle2, User, Camera, CameraOff, Keyboard, ExternalLink, Receipt, Calculator, Sparkles, Route, IndianRupee, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import QrScanner from "@/components/QrScanner";

/**
 * Extract a Pop Panda code (PP-M-XXXXXX or R-XXXXXXXX) from any decoded QR text.
 * Members historically use `qrcode.react` which encodes the raw `member_code` string,
 * but this is defensive in case QR contents ever change to a URL.
 */
function extractCode(text) {
  if (!text) return null;
  const s = String(text).trim();
  // Direct member code
  const m1 = s.match(/PP-M-\d{4,}/i);
  if (m1) return m1[0].toUpperCase();
  // Redemption code
  const m2 = s.match(/R-[A-F0-9]{4,}/i);
  if (m2) return m2[0].toUpperCase();
  // URL like /m/<code>
  try {
    const u = new URL(s);
    const parts = u.pathname.split("/").filter(Boolean);
    for (const p of parts) {
      if (/^PP-M-\d+$/i.test(p)) return p.toUpperCase();
      if (/^R-[A-F0-9]+$/i.test(p)) return p.toUpperCase();
    }
  } catch { /* not a URL */ }
  // Fallback to raw string (might still be a valid phone/email lookup)
  return s;
}

export default function AdminScanner() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [creditMode, setCreditMode] = useState("auto"); // auto | manual
  const [amount, setAmount] = useState("");
  const [manualCoins, setManualCoins] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [manualScanMode, setManualScanMode] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const [rewardsExpanded, setRewardsExpanded] = useState(false);
  const [memberRewards, setMemberRewards] = useState([]);
  const [preview, setPreview] = useState(null); // {amount_inr, coins, slab_matched}
  const lastScannedRef = useRef({ value: null, at: 0 });
  const previewDebounceRef = useRef(null);

  const lookup = useCallback(async (rawCode) => {
    const raw = (rawCode ?? "").trim();
    if (!raw) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/scanner/lookup", { code: raw });
      setResult(data);
      setCode(extractCode(raw) || raw);
      // Load rewards list if member
      if (data.type === "member") {
        try {
          const { data: rl } = await api.get("/admin/redemptions?status_filter=pending");
          setMemberRewards((rl || []).filter((r) => r.member_id === data.member.id));
        } catch { setMemberRewards([]); }
      } else {
        setMemberRewards([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  const onScanResult = useCallback((decodedText) => {
    // Guard against duplicate rapid decodes
    const now = Date.now();
    if (lastScannedRef.current.value === decodedText && now - lastScannedRef.current.at < 3000) return;
    lastScannedRef.current = { value: decodedText, at: now };
    const parsed = extractCode(decodedText);
    toast.success("QR detected");
    lookup(parsed || decodedText);
  }, [lookup]);

  const onManualSubmit = (e) => {
    e?.preventDefault?.();
    lookup(code);
  };

  const credit = async () => {
    const isManual = creditMode === "manual";
    if (isManual && !manualCoins) { toast.error("Enter coin amount"); return; }
    if (!isManual && !amount) { toast.error("Enter bill amount"); return; }
    setBusy(true);
    try {
      const payload = {
        member_id: result.member.id,
        reason: isManual ? "manual" : "purchase",
        note,
        credit_mode: creditMode,
      };
      if (isManual) payload.coins = parseInt(manualCoins);
      else payload.amount_inr = parseFloat(amount);
      const { data } = await api.post("/admin/members/adjust-coins", payload);
      toast.success(`Credited ${data.coins_credited} coins. New balance: ${data.new_balance}`);
      setResult((r) => ({ ...r, member: { ...r.member, pop_coins: data.new_balance } }));
      setAmount(""); setManualCoins(""); setNote(""); setPreview(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Debounced preview lookup while typing bill amount
  useEffect(() => {
    if (creditMode !== "auto" || !amount || parseFloat(amount) <= 0) { setPreview(null); return; }
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/members/preview-credit", { amount_inr: parseFloat(amount) });
        setPreview(data);
      } catch { setPreview(null); }
    }, 250);
    return () => { if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current); };
  }, [amount, creditMode]);

  const redeemLoyalty = async () => {
    setBusy(true);
    try {
      await api.post("/admin/loyalty/redeem", { member_id: result.member.id });
      toast.success("Loyalty reward redeemed. Journey reset.");
      // refresh result
      const { data } = await api.post("/admin/scanner/lookup", { code: result.member.member_code });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const claim = async (redemptionCode) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/redemptions/${redemptionCode}/claim`);
      toast.success("Reward marked as claimed");
      if (result?.type === "redemption") {
        setResult((r) => ({ ...r, redemption: data.redemption }));
      } else {
        setMemberRewards((prev) => prev.map((r) => (r.code === redemptionCode ? data.redemption : r)));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const scanNext = () => {
    setResult(null); setCode(""); setAmount(""); setManualCoins(""); setNote(""); setMemberRewards([]); setPreview(null);
    lastScannedRef.current = { value: null, at: 0 };
    setScanKey((k) => k + 1);
  };

  // When the page unmounts we rely on QrScanner's own cleanup.
  useEffect(() => () => setCameraOn(false), []);

  return (
    <div data-testid="admin-scanner-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Loyalty</div>
          <h1 className="editorial-h1 text-4xl">In-Store Scanner</h1>
          <p className="text-brand-charcoal/60 mt-2 max-w-2xl">Scan the member's Pop Panda QR code with the device camera to credit coins for a purchase or fulfil a reward. You can also type a code manually.</p>
        </div>
      </header>

      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        {/* LEFT — Scanner */}
        <div className="rounded-2xl bg-white border border-brand-line p-4 h-fit">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => { setCameraOn(true); setManualScanMode(false); }}
              className={`btn-pill text-xs flex-1 justify-center ${cameraOn && !manualScanMode ? "btn-dark" : "btn-ghost"}`}
              data-testid="scanner-mode-camera"
            ><Camera size={14} /> Camera</button>
            <button
              onClick={() => { setCameraOn(false); setManualScanMode(true); }}
              className={`btn-pill text-xs flex-1 justify-center ${!cameraOn && manualScanMode ? "btn-dark" : "btn-ghost"}`}
              data-testid="scanner-mode-manual"
            ><Keyboard size={14} /> Manual</button>
          </div>

          {cameraOn && (
            <QrScanner
              active={cameraOn}
              onResult={onScanResult}
              onError={() => { /* error shown inside component */ }}
              resetKey={scanKey}
            />
          )}

          {!cameraOn && !manualScanMode && (
            <div className="rounded-2xl border-2 border-dashed border-brand-line p-8 text-center">
              <Camera size={36} className="mx-auto text-brand-charcoal/40" />
              <div className="mt-3 font-medium">Ready to scan</div>
              <p className="text-xs text-brand-charcoal/55 mt-1 mb-4">Turn on the camera to start scanning member QR codes.</p>
              <button onClick={() => setCameraOn(true)} className="btn-pill btn-dark text-xs" data-testid="scanner-start-camera-btn"><Camera size={14} /> Start camera</button>
              <button onClick={() => setManualScanMode(true)} className="btn-pill btn-ghost text-xs ml-2" data-testid="scanner-manual-mode-btn"><Keyboard size={14} /> Type code</button>
            </div>
          )}

          {manualScanMode && (
            <form onSubmit={onManualSubmit} className="space-y-3" data-testid="scanner-form">
              <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Member ID / Phone / Email / Redemption code</label>
              <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} className="pp-input flex-1" placeholder="PP-M-000001 or R-XXXXXXXX" data-testid="scanner-input" autoFocus />
                <button disabled={busy || !code.trim()} className="btn-pill btn-dark text-xs" data-testid="scanner-lookup-btn"><Scan size={14} /> Lookup</button>
              </div>
            </form>
          )}

          {cameraOn && (
            <div className="mt-3 flex items-center justify-between text-xs">
              <button onClick={() => setCameraOn(false)} className="text-brand-charcoal/60 hover:text-brand-charcoal inline-flex items-center gap-1" data-testid="scanner-stop-camera-btn">
                <CameraOff size={12} /> Stop camera
              </button>
              {result && (
                <button onClick={scanNext} className="btn-pill btn-dark text-xs" data-testid="scanner-reset-btn">Scan next</button>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Result */}
        <div>
          {!result && (
            <div className="rounded-2xl bg-white border border-brand-line p-10 text-center text-brand-charcoal/45" data-testid="scanner-empty-state">
              <Scan size={40} className="mx-auto mb-3 text-brand-charcoal/30" />
              <div className="font-medium text-brand-charcoal/70">Point the camera at a member's QR code</div>
              <div className="text-xs mt-1">Details will appear here as soon as a code is detected.</div>
            </div>
          )}

          {result?.type === "member" && (
            <div className="rounded-2xl bg-white border border-brand-line p-6" data-testid="scanner-member-result">
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 rounded-full bg-brand-yellow/20 grid place-items-center"><User size={30} className="text-brand-charcoal" /></div>
                <div className="flex-1">
                  <div className="font-display text-2xl">{result.member.name}</div>
                  <div className="font-mono text-xs text-brand-charcoal/55">{result.member.member_code}</div>
                  <div className="text-sm text-brand-charcoal/70 mt-1">{result.member.email} · {result.member.phone}</div>
                  <div className="mt-3 flex items-center flex-wrap gap-2">
                    <div className="inline-flex items-center gap-2 rounded-xl bg-brand-yellow/15 px-4 py-2">
                      <Coins size={16} /> <span className="font-display text-2xl">{result.member.pop_coins ?? 0}</span> <span className="text-brand-charcoal/60 text-sm">coins</span>
                    </div>
                    <span className="inline-flex items-center rounded-xl bg-brand-charcoal/5 px-3 py-1 text-xs uppercase tracking-widest capitalize">{result.member.tier || "bronze"} tier</span>
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${result.member.status === "blocked" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{result.member.status}</span>
                  </div>
                </div>
              </div>

              <hr className="my-6 border-brand-line" />

              {/* Credit — dual mode */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Award Pop Coins</div>
                  <div className="inline-flex rounded-full bg-brand-muted p-1" data-testid="credit-mode-toggle">
                    <button
                      onClick={() => { setCreditMode("auto"); setManualCoins(""); }}
                      data-testid="credit-mode-auto"
                      className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest transition ${creditMode === "auto" ? "bg-brand-charcoal text-brand-cream" : "text-brand-charcoal/60"}`}
                    ><Receipt size={11} className="inline mr-1" /> Bill amount</button>
                    <button
                      onClick={() => { setCreditMode("manual"); setAmount(""); setPreview(null); }}
                      data-testid="credit-mode-manual"
                      className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest transition ${creditMode === "manual" ? "bg-brand-charcoal text-brand-cream" : "text-brand-charcoal/60"}`}
                    ><Coins size={11} className="inline mr-1" /> Manual coins</button>
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {creditMode === "auto" ? (
                    <motion.div key="auto" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <div className="grid sm:grid-cols-[1fr_1fr] gap-3">
                        <div>
                          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Bill amount (₹)</label>
                          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pp-input" data-testid="scanner-amount-input" placeholder="e.g. 275" autoFocus />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Bill # / note (optional)</label>
                          <input value={note} onChange={(e) => setNote(e.target.value)} className="pp-input" data-testid="scanner-note-input" placeholder="Bill #1234" />
                        </div>
                      </div>

                      {/* Live preview card */}
                      <AnimatePresence>
                        {preview && preview.coins > 0 && (
                          <motion.div
                            key="prev"
                            initial={{ opacity: 0, y: 8, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            className="mt-4 rounded-2xl border border-brand-yellow/40 bg-gradient-to-br from-brand-yellow/15 to-brand-yellow/5 p-4 flex items-center gap-4"
                            data-testid="credit-preview"
                          >
                            <div className="h-14 w-14 rounded-full bg-brand-yellow grid place-items-center shadow-md">
                              <Sparkles className="text-brand-charcoal" size={22} />
                            </div>
                            <div className="flex-1">
                              <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Will award</div>
                              <div className="font-display text-3xl leading-none">
                                <span data-testid="preview-coins">{preview.coins}</span>
                                <span className="text-sm text-brand-charcoal/60 ml-1">Pop Coins</span>
                              </div>
                              {preview.slab_matched && (
                                <div className="text-[11px] text-brand-charcoal/55 mt-1">
                                  Slab: ₹{preview.slab_matched.min_inr}–₹{preview.slab_matched.max_inr}
                                  {preview.slab_matched.fixed_coins !== undefined && preview.slab_matched.fixed_coins !== null ? ` · fixed ${preview.slab_matched.fixed_coins}` : ` · ×${preview.slab_matched.multiplier || 1}`}
                                </div>
                              )}
                            </div>
                            <button onClick={credit} disabled={busy} className="btn-pill btn-dark text-xs" data-testid="scanner-credit-btn">
                              <CheckCircle2 size={12} /> Confirm
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {(!preview || preview.coins === 0) && amount && (
                        <div className="mt-3 text-xs text-brand-charcoal/55">Calculating…</div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div key="manual" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                      <div>
                        <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Coins to add</label>
                        <input type="number" min="1" value={manualCoins} onChange={(e) => setManualCoins(e.target.value)} className="pp-input" data-testid="scanner-manual-coins-input" placeholder="e.g. 25" autoFocus />
                        <div className="text-[11px] text-brand-charcoal/50 mt-1"><Calculator size={11} className="inline -mt-0.5 mr-1" /> Direct credit — no bill required</div>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reason (optional)</label>
                        <input value={note} onChange={(e) => setNote(e.target.value)} className="pp-input" data-testid="scanner-manual-note-input" placeholder="e.g. Festival bonus" />
                      </div>
                      <button onClick={credit} disabled={busy || !manualCoins} className="btn-pill btn-dark text-xs h-fit" data-testid="scanner-manual-credit-btn"><Coins size={14} /> Add Coins</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Loyalty status */}
              {result.loyalty_current && (
                <div className="mt-6 rounded-2xl border border-brand-line p-4" data-testid="scanner-loyalty-block">
                  <div className="flex items-center gap-2 mb-2">
                    <Route size={14} className="text-brand-charcoal/60" />
                    <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Popsicle Loyalty</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-sm">Cycle #{result.loyalty_current.cycle_number} — <span className="font-mono">{result.loyalty_current.progress}/{result.loyalty_current.target}</span> popsicles</div>
                      <div className="mt-2 h-2 rounded-full bg-brand-muted overflow-hidden">
                        <div className="h-full bg-brand-yellow" style={{ width: `${Math.min(100, (result.loyalty_current.progress / result.loyalty_current.target) * 100)}%` }} />
                      </div>
                    </div>
                    {result.loyalty_pending_reward ? (
                      <button onClick={redeemLoyalty} disabled={busy} className="btn-pill btn-dark text-xs whitespace-nowrap" data-testid="scanner-loyalty-redeem-btn"><Gift size={12} /> Redeem free popsicle</button>
                    ) : (
                      <div className="text-xs text-brand-charcoal/50 whitespace-nowrap">{result.loyalty_current.target - result.loyalty_current.progress} to go</div>
                    )}
                  </div>
                  {result.loyalty_pending_reward && (
                    <div className="mt-3 text-xs text-brand-charcoal/60">Reward code: <span className="font-mono text-brand-charcoal">{result.loyalty_pending_reward.reward_code}</span></div>
                  )}
                </div>
              )}

              {/* Pending rewards for member */}
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Available rewards ({memberRewards.length})</div>                  {memberRewards.length > 0 && (
                    <button onClick={() => setRewardsExpanded((v) => !v)} className="text-xs text-brand-charcoal/60 hover:text-brand-charcoal">{rewardsExpanded ? "Hide" : "Show"}</button>
                  )}
                </div>
                {memberRewards.length === 0 ? (
                  <div className="mt-2 text-sm text-brand-charcoal/45">No pending rewards.</div>
                ) : (rewardsExpanded && (
                  <ul className="mt-3 space-y-2">
                    {memberRewards.map((r) => (
                      <li key={r.id} className="flex items-center gap-3 rounded-xl border border-brand-line px-3 py-2" data-testid={`scanner-member-reward-${r.code}`}>
                        <Gift size={14} className="text-brand-yellow" />
                        <div className="flex-1">
                          <div className="text-sm">{r.reward_title}</div>
                          <div className="text-[11px] font-mono text-brand-charcoal/55">{r.code}</div>
                        </div>
                        <button onClick={() => claim(r.code)} disabled={busy} className="btn-pill btn-dark text-[10px]" data-testid={`scanner-claim-reward-${r.code}`}><CheckCircle2 size={12} /> Redeem</button>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>

              {/* Payments + Pop Reveals + Vault snapshot */}
              {(result.recent_payments?.length > 0 || result.recent_pop_reveals?.length > 0 || result.vault_contributions?.length > 0) && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="scanner-activity-snapshot">
                  <div className="rounded-xl border border-brand-line p-3">
                    <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 flex items-center gap-1"><IndianRupee size={10} /> Payments</div>
                    <div className="text-lg font-display mt-0.5">{(result.recent_payments || []).length}</div>
                    {(result.recent_payments || []).slice(0, 3).map((p) => (
                      <div key={p.id} className="mt-1 text-[11px] text-brand-charcoal/70 flex justify-between gap-2">
                        <span className="truncate">₹{Number(p.amount).toFixed(0)} {p.outlet_name?.split(" ")[0] || ""}</span>
                        <span className="text-brand-charcoal/45 shrink-0">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ""}</span>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl border border-brand-line p-3">
                    <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 flex items-center gap-1"><Sparkles size={10} /> Pop Reveals</div>
                    <div className="text-lg font-display mt-0.5">{(result.recent_pop_reveals || []).length}</div>
                    {(result.recent_pop_reveals || []).slice(0, 3).map((c) => {
                      const s = c.reward_snapshot || {};
                      return (
                        <div key={c.id} className="mt-1 text-[11px] text-brand-charcoal/70 truncate">
                          {s.is_win ? `🎁 ${s.name}` : "🥲 Better luck"}
                        </div>
                      );
                    })}
                  </div>
                  <div className="rounded-xl border border-brand-line p-3">
                    <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 flex items-center gap-1"><Lock size={10} /> Vault</div>
                    <div className="text-lg font-display mt-0.5">{(result.vault_contributions || []).reduce((s, v) => s + (v.value || 0), 0)}</div>
                    <div className="text-[11px] text-brand-charcoal/60">points contributed</div>
                  </div>
                </div>
              )}

              {/* Quick link to full member page */}
              <div className="mt-6">
                <Link to={`/admin/members`} className="text-xs text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1" data-testid="scanner-view-profile">
                  View full member profile <ExternalLink size={11} />
                </Link>
              </div>
            </div>
          )}

          {result?.type === "redemption" && (
            <div className="rounded-2xl bg-white border border-brand-line p-6" data-testid="scanner-redemption-result">
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 rounded-full bg-brand-yellow/20 grid place-items-center"><Gift size={30} className="text-brand-charcoal" /></div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reward Redemption</div>
                  <div className="font-display text-2xl mt-1">{result.redemption.reward_title}</div>
                  <div className="font-mono text-sm mt-1">{result.redemption.code}</div>
                  <div className="mt-2 text-sm text-brand-charcoal/70">Member: {result.member?.name} <span className="font-mono">({result.member?.member_code})</span></div>
                  <div className="mt-1 text-xs text-brand-charcoal/50">Cost: {result.redemption.cost_coins} coins · Created {new Date(result.redemption.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${result.redemption.status === "claimed" ? "bg-emerald-100 text-emerald-700" : "bg-brand-yellow/25 text-brand-charcoal"}`}>{result.redemption.status}</span>
              </div>

              <hr className="my-6 border-brand-line" />

              {result.redemption.status === "pending" ? (
                <button onClick={() => claim(result.redemption.code)} disabled={busy} className="btn-pill btn-dark text-xs" data-testid="scanner-claim-btn"><CheckCircle2 size={14} /> Mark as fulfilled</button>
              ) : (
                <div className="text-emerald-700 text-sm inline-flex items-center gap-2"><CheckCircle2 size={14} /> Already claimed on {new Date(result.redemption.claimed_at).toLocaleString()}</div>
              )}
            </div>
          )}

          {result && (
            <div className="mt-4 flex justify-end">
              <button onClick={scanNext} className="btn-pill btn-ghost text-xs" data-testid="scanner-next-btn"><Scan size={12} /> Scan next member</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
